EventEmitter = require('backbone').Events
MembersLoader = require 'views/enterprise/dashboard/members-loader'
FilterStringBuilder = require 'views/enterprise/dashboard/filter-string-builder'
Promise = require 'bluebird'
renderableItems = require 'views/enterprise/dashboard/renderable-items'
_ = require 'underscore'

# When loading members in bulk, like we do for this page, we don't include
# the idOrganizations field, because it takes too long to check permissions
# on every team for the requesting user. This means almost every user in
# @model.memberList will have an empty organizationList attribute, and so we
# need to manually keep track.
class OrgsPerMember
  constructor: (orgs) ->
    @_hash = _.reduce orgs, (memo, org) ->
      for membership in org.get('memberships')
        memo[membership.idMember] ?= []
        memo[membership.idMember].push(org)
      memo
    , {}

  get: (idMember) ->
    @_hash[idMember] ?= []
    @_hash[idMember]

module.exports.Member = class MemberItemList
  _.extend @::, EventEmitter

  DEFAULT_SORT_FIELD: 'fullName'
  DEFAULT_SORT_ORDER: 'ascending'

  constructor: (enterprise) ->
    @enterprise = enterprise
    @_mainPaginatedList = new MembersLoader(@enterprise)
    @paginatedList = @_mainPaginatedList

    @orgsPerMember = new OrgsPerMember(@enterprise.organizationList.models)
    @filterStringBuilder = new FilterStringBuilder(renderableItems.Member::API_SEARCH_FIELDS)
    @renderables = {}
    for model in @enterprise.memberList.models
      renderable = new renderableItems.Member(model, @orgsPerMember.get(model.get('id')))
      @renderables[renderable.id] = renderable

    @sortField = MemberItemList::DEFAULT_SORT_FIELD
    @sortOrder = MemberItemList::DEFAULT_SORT_ORDER
    @resetState()

  resetState: ->
    @filterStringBuilder.clearAll()
    @updateFilter('search', "")

  _isUsingDefaultSorting: ->
    @sortField == MemberItemList::DEFAULT_SORT_FIELD && \
      @sortOrder == MemberItemList::DEFAULT_SORT_ORDER

  search: ->
    @incomingPaginatedList?.abortLoad()

    if @filterStringBuilder.getActiveFilters().length == 0 && @_isUsingDefaultSorting()
      @incomingPaginatedList = @_mainPaginatedList
      # Upon instantiation we'll do the search for the main list, then
      # subsequently it'll already be ready to use.
      if @incomingPaginatedList.totalLength > 0
        @_swapInNewList()
        @trigger('loaded')
        return
    else
      @incomingPaginatedList = new MembersLoader @enterprise, {
        @filterStringBuilder
        @sortField
        @sortOrder
      }

    @loadNextPage()

  sort: (field, order) ->
    @sortField = field
    @sortOrder = order
    @search()

  getSortField: ->
    @sortField

  getSortOrder: ->
    @sortOrder

  updateFilter: (type, value) ->
    @filterStringBuilder.update(type, value)
    @search()

  toggleFilter: (type, value) ->
    @filterStringBuilder.toggle(type, value)
    @search()

  clearFilter: (type) ->
    @filterStringBuilder.clear(type)
    @search()

  getFilterValueMap: ->
    @filterStringBuilder.getState()

  getActiveFilters: ->
    @filterStringBuilder.getActiveFilters()

  _addLoadedRenderables: (members) ->
    for m in members when !@renderables[m.get('id')]?
      renderable = new renderableItems.Member(m, @orgsPerMember.get(m.get('id')))
      renderable.selected = @paginatedList.loadAsSelected
      @renderables[renderable.id] = renderable

  _swapInNewList: ->
    return if @paginatedList == @incomingPaginatedList
    @paginatedList = @incomingPaginatedList
    @deselectAllItems()

  loadNextPage: ->
    return Promise.resolve([]) unless @incomingPaginatedList.hasNext
    @trigger if @incomingPaginatedList == @paginatedList
        'loadingNext'
      else
        'loading'
    @incomingPaginatedList.loadNext()
      .tap (members) =>
        @_addLoadedRenderables(members)
        @_swapInNewList()
        @trigger('loaded')
      .catch Promise.CancellationError, (->)
      .catch (err) =>
        @_swapInNewList()
        @trigger('loadError')

  hasNextPage: ->
    @paginatedList.hasNext

  items: ->
    _.map @paginatedList.items, (item) =>
      @renderables[item.get('id')]

  deselectAllItems: ->
    _.forEach @renderables, (member) -> member.selected = false

  itemSelected: (newlySelectedItem, selectRange) ->
    return if newlySelectedItem == @previousSelectedItem

    if selectRange && @previousSelectedItem?
      items = @items()
      indexOfNew = items.indexOf(newlySelectedItem)
      indexOfPrevious = items.indexOf(@previousSelectedItem)
      for i in [indexOfNew..indexOfPrevious]
        items[i].selected = newlySelectedItem.selected
      return

    @previousSelectedItem = newlySelectedItem

  selectedItemsLength: ->
    result = @_selectedRenderables().length
    if @paginatedList.loadAsSelected
      result += @paginatedList.lengthOfPending
    result

  _selectedRenderables: ->
    _.filter @items(), (member) ->
      member.selected

  selectedItems: ->
    if !@paginatedList.loadAsSelected
      Promise.resolve(@_selectedRenderables())
    else
      @paginatedList.loadAll(@_addLoadedRenderables.bind(@))
      .then(@_selectedRenderables.bind(@))

  _setSelected: (value) ->
    return unless @paginatedList?
    @paginatedList.loadAsSelected = value

    for toMark in @items()
      toMark.selected = value

  selectAll: ->
    @_setSelected(true)

  deselect: ->
    @_setSelected(false)

  lengthOfSearch: -> @paginatedList.totalLength
  lengthOfLoaded: -> @paginatedList.items.length
  totalLength: -> @_mainPaginatedList.totalLength

module.exports.Team = class TeamItemList
  _.extend @::, EventEmitter

  constructor: (enterprise) ->
    @enterprise = enterprise
    @allItems = enterprise.organizationList.models
    @resetState()

  add: (team) ->
    @allItems = @enterprise.organizationList.models
    renderable = new renderableItems.Team(team)
    @renderables[renderable.id] = renderable
    @updateFilter('search', @searchTerm)

  resetState: ->
    @renderables = {}
    for item in @allItems
      renderable = new renderableItems.Team(item)
      @renderables[renderable.id] = renderable

    @updateFilter('search', "")

  # Currently only for search. The type argument is not used but it's there to
  # keep the interface consistent with MemberItemList's.
  updateFilter: (type, searchTerm) ->
    @searchTerm = searchTerm

    matches = _.filter @allItems, (item) =>
      renderable = @renderables[item.get('id')]
      renderable.isSearchMatch(searchTerm)

    @_items = _.map matches, (item) =>
      @renderables[item.get('id')]

    @trigger('loaded')

  loadNextPage: -> Promise.resolve([])
  hasNextPage: -> false
  items: -> @_items
  lengthOfSearch: -> @_items.length
  lengthOfLoaded: -> @_items.length
  totalLength: -> @allItems.length
  getActiveFilters: -> []



// WEBPACK FOOTER //
// ./app/scripts/views/enterprise/dashboard/item-list.coffee